/**
 * Hook for generating CURP from personal details
 */
import { useMemo } from "react";
import { useSignup as useSignupContext } from "../../SignupContext";
import { handleApiError, InvalidStateError } from "../../utils/Errors";
import { useApiRequest } from "../../utils/useApiRequest";
/**
 * Hook for generating a CURP based on personal information
 * @returns RequestState object for CURP generation
 */
export function useGenerateCurp() {
    const { apiClient, setFormValues, formValues } = useSignupContext();
    const { state: generateCurpState, execute: executeGenerateCurp, reset: resetGenerateCurpState, } = useApiRequest();
    return useMemo(() => ({
        execute: async (curpDetails) => {
            // Validate required fields
            const missingFields = [];
            if (!curpDetails.firstName)
                missingFields.push("firstName");
            if (!curpDetails.paternalLastName)
                missingFields.push("paternalLastName");
            if (!curpDetails.gender)
                missingFields.push("gender");
            if (!curpDetails.dayOfBirth)
                missingFields.push("dayOfBirth");
            if (!curpDetails.monthOfBirth)
                missingFields.push("monthOfBirth");
            if (!curpDetails.yearOfBirth)
                missingFields.push("yearOfBirth");
            if (!curpDetails.stateOfBirth)
                missingFields.push("stateOfBirth");
            if (missingFields.length > 0) {
                throw new InvalidStateError("Cannot generate CURP with incomplete data", missingFields);
            }
            return executeGenerateCurp(async (details) => {
                // Pass the phone number from form values to the API client
                const response = await apiClient.generateCurp(details, formValues.phoneNumber);
                if (response.ok) {
                    const data = (await response.json());
                    // Update the main curp value in the form state
                    if (data.isCurpValid && data.generatedCurp) {
                        setFormValues.setCurp(data.generatedCurp);
                    }
                    return data;
                }
                else {
                    const error = await handleApiError(response);
                    throw error;
                }
            }, curpDetails);
        },
        isLoading: generateCurpState.isLoading,
        error: generateCurpState.error,
        data: generateCurpState.data,
        reset: resetGenerateCurpState,
    }), [
        apiClient,
        executeGenerateCurp,
        generateCurpState,
        resetGenerateCurpState,
        setFormValues.setCurp,
        formValues.phoneNumber,
    ]);
}
